<template>
   <v-container fluid>
     <h2>
       {{ $tc('notification', 2) }}
     </h2>
     <div>
       {{ $tc('notification_rules_ui.description') }}
     </div>
     <div>
       <v-sheet outlined rounded class="px-4 py-2 mt-3">
         <v-form @submit.prevent="onSubmit" id="notification-rule-form">
           <h4>{{ $t('notification_rules_ui.create_a_new_notification_rule') }}</h4>
           <v-select
             :label="$t('notification_rules_ui.target')"
             v-model="newRule.target"
             :items="targetOptions"
             :error-messages="targetErrors" />
           <v-select
             :label="$t('notification_rules_ui.event')"
             v-model="newRule.event"
             :items="eventOptions"
             :error-messages="eventErrors" />
           <div v-if="newRule.event != null">
             <v-radio-group
               :label="$t('notification_rules_ui.rules')"
               v-model="newRule.rule"
               :items="rulesOptions"
               :error-messages="ruleErrors">
               <v-radio
                 v-for="item in rulesOptions"
                 :key="item.value"
                 :label="item.text"
                 :value="item.value"/>
             </v-radio-group>
           </div>
           <div>{{ $t('notification_rules_ui.delivery') }}</div>
           <v-checkbox
             v-for="item in deliveryOptions"
             multiple
             :key="item.value"
             :label="item.text"
             :value="item.value"
             v-model="newRule.delivery"
             :error-messages="deliveryErrors">
           </v-checkbox>
           <div class="d-flex justify-end">
             <v-btn type="submit" class="my-2" color="primary" :loading="saving">
               <v-icon>mdi-content-save</v-icon>
               {{$t('create_rule')}}
             </v-btn>
           </div>
         </v-form>
       </v-sheet>
       <v-divider class="my-4"/>
       <notification-rule-index ref="notification-rule-index" />
       <v-divider class="my-4"/>
       <recent-notification-index />
     </div>
   </v-container>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import NotificationRuleIndex from '@/components/notification/NotificationRuleIndex'
import RecentNotificationIndex from '@/components/notification/RecentNotificationIndex'
import NotificationRuleRecord from '@/orm/models/NotificationRuleRecord'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import { map, reduce, filter } from 'lodash'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'NotificationRules',
  components: {
    RecentNotificationIndex,
    NotificationRuleIndex
  },
  mixins: [
    CurrentUserMixin,
    BrowserUtilitiesMixin,
    ValidationMixin
  ],
  validations: {
    newRule: {
      target: {
        required
      },
      event: {
        required
      },
      rule: {
        required
      },
      delivery: {
        required
      }
    }
  },
  data () {
    return {
      saving: false,
      products: [],
      newRule: this.initNewRule()
    }
  },
  methods: {
    /**
     * @return NotificationRuleRecord
     */
    initNewRule () {
      return this.$orm('NotificationRule').createRecord({ delivery: ['email'] })
    },

    /**
     * Sets the products property to an array of ProductRecords on this instance
     *
     * @return undefined
     */
    fetchProducts () {
      const requestParams = {
        customer_id: map(this.user.customers, x => x.id).join(','),
        expand: 'customers',
        fields: 'id,name,customers.name,customers.parent_id',
        'per-page': 100
      }
      this.$orm('Product').findAll(requestParams)
        .then(resp => {
          this.products = resp.items
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.newRule.rules = [this.newRule.rule]
      this.newRule.user_id = this.user.id

      this.saving = true

      this.$gtag.event('create', { event_category: 'Notification' })
      this.$orm('NotificationRule').create(this.newRule)
        .then(resp => {
          // Reset the rule object
          this.newRule = this.initNewRule()
          // Reset the vuelidate validation
          this.$v.$reset()
          // Reload the list of rules
          this.$refs['notification-rule-index'].refreshResults()
          // Show success
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$tc('notification_rule')} ${this.$t('generic_save_success')}`
          )
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.saving = false
        })
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('notification', 2))
    this.fetchProducts()
  },
  watch: {
    'newRule.target' (newTarget, oldTarget) {
      // Clear event when switching target types
      if (newTarget && oldTarget) {
        const newIsCustomer = newTarget.startsWith('Customer(')
        const oldIsCustomer = oldTarget.startsWith('Customer(')
        if (newIsCustomer !== oldIsCustomer) {
          this.newRule.event = null
          this.newRule.rule = null
        }
      }
    },
    'newRule.event' (newEvent) {
      // Auto-select immediate rule for route plan events
      if (newEvent && newEvent.startsWith('route_plan_upload')) {
        this.newRule.rule = 'duration:0 seconds'
      } else if (newEvent && !newEvent.startsWith('route_plan_upload')) {
        // Clear rule for violation events (let user choose)
        this.newRule.rule = null
      }
    }
  },
  computed: {
    /**
     * Constructs an array of targets including both products (for violations)
     * and customers (for route plan uploads).
     *
     * @return {Array<Object>}
     */
    targetOptions () {
      const targets = []

      // Product targets for violation events
      targets.push(...reduce(this.products, (acc, product) => {
        const targetName = `Product(${product.id})`
        let name = product.name

        if (product.customers.length > 0) {
          const parentCustomer = product.customers.find(customer => customer.parent_id === null)
          if (parentCustomer) {
            name = `${product.name} (${parentCustomer.name})`
          }
        }

        acc.push({ text: name, value: targetName })
        return acc
      }, []))

      // Customer targets for route plan upload events
      targets.push(...map(this.user.customers, customer => ({
        text: `${customer.name} - Route Plans`,
        value: `Customer(${customer.id})`
      })))

      return targets
    },

    /**
     * Populates the event options selection dialog based on selected target.
     *
     * @returns {*[]}
     */
    eventOptions () {
      if (!this.newRule.target) {
        return []
      }

      const results = []
      const eventOptions = NotificationRuleRecord.getEventOptions()
      const filterFn = (opts, level) => filter(opts, opt => NotificationRuleRecord.getEventLevel(opt) === level)
      const mapFn = opts => map(opts, opt => ({ value: opt, text: this.$t(opt) }))
      const routePlanEventsFn = opts => filter(opts, opt => opt.startsWith('route_plan_upload'))

      // If target is a Product, show violation events
      if (this.isProductTarget) {
        if (this.user.can('view_rootentity')) {
          results.push({ header: this.$tc('violation', 2) })
        }
        results.push(...mapFn(filterFn(eventOptions, RouteViolationRecord.LEVEL_FATAL)))

        if (this.customerContext.getSetting('enableSimulatedTemperatureForecast') === 1) {
          results.push({ header: this.$tc('forecast_staging_violation', 2) })
          results.push(...mapFn(filterFn(eventOptions, RouteViolationRecord.LEVEL_FORECAST_STAGING)))
        }

        if (
          this.customerContext.getSetting('enableSimulatedTemperatureForecast') === 1 &&
          this.customerContext.getSetting('enableSimulatedTemperatureForecastFullRoute') === 1
        ) {
          results.push({ header: this.$tc('forecast_violation', 2) })
          results.push(...mapFn(filterFn(eventOptions, RouteViolationRecord.LEVEL_FORECAST)))
        }
      }

      // If target is a Customer, show route plan upload events
      if (this.isCustomerTarget) {
        results.push({ header: this.$t('route_plan_uploads') })
        results.push(...mapFn(routePlanEventsFn(eventOptions)))
      }

      return results
    },

    targetErrors () {
      return this.validationError({
        prop: this.$v.newRule.target,
        message: this.$t('please_select_one')
      })
    },

    eventErrors () {
      return this.validationError({
        prop: this.$v.newRule.event,
        message: this.$t('please_select_one')
      })
    },

    deliveryErrors () {
      return this.validationError({
        prop: this.$v.newRule.delivery,
        message: this.$t('please_select_one')
      })
    },

    ruleErrors () {
      return this.validationError({
        prop: this.$v.newRule.rule,
        message: this.$t('please_select_one')
      })
    },
    /**
     * Constructs an array of violation rules to choose from once the event type
     * is defined (e.g. fatal_violation, forecast_violation, etc...)
     *
     * @return {Array<Object>}
     */
    rulesOptions () {
      // Route plan events are immediate (no duration options)
      if (this.isRoutePlanEvent) {
        return [{ text: this.$t('immediate'), value: 'duration:0 seconds' }]
      }

      // Violation events have duration options
      return reduce(NotificationRuleRecord.getRulesOptions(), (acc, rule) => {
        acc.push({ text: this.$t(`${this.eventType}:${rule}`), value: rule })
        return acc
      }, [])
    },

    /**
     * @return {String}
     */
    eventType () {
      const level = NotificationRuleRecord.getEventLevel(this.newRule.event)
      return level === 'route_plan_upload' ? level : `${level}_violation`
    },

    /**
     * @return {Boolean}
     */
    isProductTarget () {
      return this.newRule.target && this.newRule.target.startsWith('Product(')
    },

    /**
     * @return {Boolean}
     */
    isCustomerTarget () {
      return this.newRule.target && this.newRule.target.startsWith('Customer(')
    },

    /**
     * @return {Boolean}
     */
    isViolationEvent () {
      return this.newRule.event && !this.newRule.event.startsWith('route_plan_upload')
    },

    /**
     * @return {Boolean}
     */
    isRoutePlanEvent () {
      return this.newRule.event && this.newRule.event.startsWith('route_plan_upload')
    },

    /**
     * @return {Array<Object>}
     */
    deliveryOptions () {
      return [
        {
          text: `Email: (${this.user.email})`,
          value: 'email'
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
